/**
 * Royalty CC recipients admin page (A5 / F7).
 * Manage the stakeholder addresses that can be CC'd on royalty statement sends.
 * Note: royalties@peterlang.com is intentionally rejected (connected inbox → would
 * duplicate the R6 ticket).
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { getCcRecipients, addCcRecipient, toggleCcRecipient } from "@/lib/royalty-api";
import { isValidEmail } from "@/lib/validation";
import { Loader2 } from "lucide-react";

export default function RoyaltyCcSettingsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");

  const query = useQuery({
    queryKey: ["royalty-cc-recipients", "all"],
    queryFn: () => getCcRecipients(false),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["royalty-cc-recipients"] });
  };

  const addMutation = useMutation({
    mutationFn: () => addCcRecipient(email.trim(), displayName.trim() || undefined),
    onSuccess: () => {
      toast({ title: "CC recipient added", description: email.trim() });
      setEmail("");
      setDisplayName("");
      invalidate();
    },
    onError: (err: Error) => toast({ title: "Could not add recipient", description: err.message, variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => toggleCcRecipient(id, isActive),
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast({ title: "Could not update recipient", description: err.message, variant: "destructive" }),
  });

  const canAdd = isValidEmail(email) && !addMutation.isPending;

  return (
    <PageWrapper>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Royalty CC Recipients</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Stakeholders that can be CC'd when sending royalty statements. Active recipients appear as
            checkboxes in the send dialogs. royalties@peterlang.com cannot be added — it auto-creates a
            ticket, which the tool already does directly.
          </p>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Add recipient</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={email.length > 0 && !isValidEmail(email)}
              />
              <Input
                placeholder="Display name (optional)"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <Button onClick={() => addMutation.mutate()} disabled={!canAdd}>
                {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
              </Button>
            </div>
            {email.length > 0 && !isValidEmail(email) && (
              <p className="text-sm text-red-600 mt-1">Please enter a valid email address.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {query.isLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            ) : !query.data || query.data.length === 0 ? (
              <p className="text-center text-muted-foreground py-10">No CC recipients yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {query.data.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-sm">{r.emailAddress}</TableCell>
                      <TableCell>{r.displayName || "—"}</TableCell>
                      <TableCell className="text-right">
                        <Switch
                          checked={r.isActive}
                          onCheckedChange={(checked) => toggleMutation.mutate({ id: r.id, isActive: checked })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
}
